import React from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { HBSidePanelProvider } from './HBSidePanelProvider';
import HBSidePanelCloseButton from './HBSidePanelCloseButton';
import HBSidePanelHeader from './HBSidePanelHeader';
import HBSidePanelBody from './HBSidePanelBody';
import { Z_INDEX } from '../../styles/variables';
import { vars } from '../../theme';
import { useSidePanelOpenState } from './hooks/useSidePanelOpenState';
import { useResizableWidth } from './hooks/useResizableWidth';
import {
    SIDE_PANEL_CLASS,
    SIDE_PANEL_RESIZE_HANDLE_CLASS_NAME,
    SIDE_PANEL_RESIZE_HANDLE_DRAGGING_CLASS_NAME
} from './HBSidePanel.consts';
import { HBSidePanelExpandButton } from './HBSidePanelExpandButton';
import { transition } from '../../styles/animations';

export interface HBSidePanelProps {
    /** Controls whether the panel is visible. Set to `true` to slide the panel in; `false` to slide it out. */
    isOpen: boolean;
    /** Enables left-edge drag handle for user-controlled width resizing. */
    isResizable?: boolean;
    /** Minimum pixel width the panel can be resized to; only applies when `isResizable` is true. */
    minWidth?: number;
    /** Called after the slide-out transition completes, not immediately when the panel starts closing. */
    onClose?: () => void;
    /** Initial pixel width of the panel; capped at 90% of viewport width via CSS. */
    width: number;
}

const HBSidePanelBase: React.FC<HBSidePanelProps> = ({
    children,
    isOpen,
    isResizable = false,
    minWidth,
    onClose,
    width
}) => {
    const { closeSidePanel, internalIsOpen, onTransitionEnd } =
        useSidePanelOpenState({
            isOpen,
            onClose
        });

    const [currentWidth] = useResizableWidth({
        initialWidth: width,
        isResizable,
        minWidth
    });

    const sidePanel = (
        <HBSidePanelProvider closeSidePanel={closeSidePanel}>
            <FixedContainer
                style={{ width: `${currentWidth}px` }}
                isOpen={internalIsOpen}
                data-state={internalIsOpen ? 'open' : 'closed'}
                data-testid="side-panel-container"
                onTransitionEnd={onTransitionEnd}
                className={SIDE_PANEL_CLASS}
            >
                {children}

                {isResizable && (
                    <ResizeHandle
                        className={SIDE_PANEL_RESIZE_HANDLE_CLASS_NAME}
                    />
                )}
            </FixedContainer>
        </HBSidePanelProvider>
    );

    return createPortal(sidePanel, document.body);
};

export const HBSidePanel = Object.assign(HBSidePanelBase, {
    Body: HBSidePanelBody,
    Header: HBSidePanelHeader,
    CloseButton: HBSidePanelCloseButton,
    ExpandButton: HBSidePanelExpandButton
});

export default HBSidePanel;

// -------------------------------- Styles -------------------------------- //
const FixedContainer = styled.div<{ isOpen: boolean }>`
    position: fixed;
    z-index: ${Z_INDEX.FULL_SCREEN};
    max-width: 90%;
    top: 0;
    right: 0;
    bottom: 0;
    background-color: ${vars.colors.surface.container};
    transform: translateX(${({ isOpen }) => (isOpen ? 0 : '100%')});
    transition: transform 240ms cubic-bezier(0.4, 0, 0.93, 0.5);
    ${({ isOpen }) =>
        !isOpen &&
        css`
            transition: transform 300ms cubic-bezier(0.08, 0.4, 0.2, 1);
        `}
    box-shadow: ${vars.shadow['50']};
    display: flex;
    flex-direction: column;
`;

const ResizeHandle = styled.div`
    width: 16px;
    position: absolute;
    z-index: ${Z_INDEX.FULL_SCREEN + 1};
    top: 0;
    left: 0;
    bottom: 0;
    cursor: ew-resize;

    border-left: 2px solid;
    border-left-color: transparent;
    ${transition('border-left-color')};
    &:hover,
    &.${SIDE_PANEL_RESIZE_HANDLE_DRAGGING_CLASS_NAME} {
        border-left-color: ${vars.colors.stroke.accent};
    }
`;
